import React, { FunctionComponent, ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { FormGroup } from 'record-it-components/form/FormGroup/FormGroup';
import { Checkbox } from 'record-it-components/input/Checkbox/Checkbox';
import { BufferedTextInput } from 'record-it-components/input/TextInput/TextInput';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { LocationInput } from 'record-it-components/input/LocationInput/LocationInput';
import { ConstructionSite } from '../../../classes/entities/ConstructionSite/constructionSiteTypes';
import {
  AnyPropertyChanged,
  useEntityPropertyChangedCallback
} from '../../../hooks/entity/useEntityPropertyChangedCallback';
import { TimeTrackingActivityCompilationSelect } from '../../timeTrackingActivityCompilation/TimeTrackingActivityCompilationSelect/TimeTrackingActivityCompilationSelect';

export type EditConstructionSiteFormProps = PropsWithoutChildren<
  ComponentProps<'div'>
> & {
  constructionSite: ConstructionSite;
  onPropertyChanged: AnyPropertyChanged<ConstructionSite>;
};

export const EditConstructionSiteForm: FunctionComponent<EditConstructionSiteFormProps> =
  React.memo((props) => {
    const { constructionSite, onPropertyChanged, ...remainingProps } =
      prepareProps({ props, ignoreChildren: true });

    const { t } = useTranslation();

    const handleNameChanged = useEntityPropertyChangedCallback(
      constructionSite,
      'name',
      onPropertyChanged
    );
    const handleArchivedChanged = useEntityPropertyChangedCallback(
      constructionSite,
      'archived',
      onPropertyChanged
    );
    const handleCompilationIdChanged = useEntityPropertyChangedCallback(
      constructionSite,
      'compilationId',
      onPropertyChanged
    );
    const handleLocationChanged = useEntityPropertyChangedCallback(
      constructionSite,
      'location',
      onPropertyChanged
    );

    return (
      <div {...remainingProps}>
        <FormGroup>
          <BufferedTextInput
            label={t('entities.ConstructionSite.name')}
            value={constructionSite.name}
            onInput={handleNameChanged}
            data-testid="EditConstructionSiteForm.NameInput"
          ></BufferedTextInput>
        </FormGroup>
        <FormGroup>
          <Checkbox
            label={t('entities.ConstructionSite.archived')}
            value={constructionSite.archived}
            onValueChanged={handleArchivedChanged}
          ></Checkbox>
        </FormGroup>
        <FormGroup>
          <TimeTrackingActivityCompilationSelect
            value={constructionSite.compilationId}
            label={t('entities.ConstructionSite.compilationId')}
            userGroupId={constructionSite.ownerUserGroupId}
            onValueChanged={handleCompilationIdChanged}
            data-testid="EditConstructionSiteForm.CompilationSelect"
          ></TimeTrackingActivityCompilationSelect>
        </FormGroup>

        <FormGroup>
          <LocationInput
            value={constructionSite.location}
            label={t('entities.ConstructionSite.location')}
            onValueChanged={handleLocationChanged}
          ></LocationInput>
        </FormGroup>
      </div>
    );
  });
